import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Toolbar,
  SelectChangeEvent,
} from '@mui/material';
import { DigitalQuestion, QuestionTypeV2 } from '../../redux/types';
import StringQuestion from './string/StringQuestion';
import SingleChoiceQuestion from './singleChoice/SingleChoiceQuestion';
import MultiChoiceQuestion from './multiChoice/MultiChoiceQuestion';
import RatioQuestion from './ratio/RatioQuestion';
import NumberRangeQuestion from './numberRange/NumberRangeQuestion';
import ChoiceWithTextQuestion from './choiceWithText/ChoiceWithTextQuestion';
import SingleChoiceWithTextQuestion from './singleChoiceWithText/SingleChoiceWithTextQuestion';
import MultiChoiceWithTextQuestion from './multiChoiceWithText/MultiChoiceWithTextQuestion';

type QuestionV2Keys = keyof typeof QuestionTypeV2;

export type Props = {
  surveyId: number;
  sectionId?: number;
  addQuestion: (question: Omit<DigitalQuestion, 'id'>) => void;
  disabledQuestions?: QuestionV2Keys[]
  disableHideAggregation?: boolean;
  enableTNA?: boolean;
}

const QuestionAdder: React.FC<Props> = ({
  surveyId,
  sectionId,
  addQuestion,
  disabledQuestions = [],
  disableHideAggregation,
  enableTNA,
}: Props) => {
  const [questionType, setQuestionType] = useState<QuestionTypeV2>(QuestionTypeV2.NumberRange);

  const handleQuestionChange = (event: SelectChangeEvent) => {
    setQuestionType(event.target.value as QuestionTypeV2);
  };

  return (
    <Paper variant="outlined" sx={{ py: 2 }}>
      <Toolbar>
        <FormControl sx={{ flexBasis: 250 }} size="small">
          <InputLabel id="select-question-type-label">Question Type</InputLabel>
          <Select<QuestionTypeV2>
            labelId="select-question-type-label"
            id="select-question-type"
            value={questionType}
            label="Select Question Type"
            onChange={handleQuestionChange}
          >
            <MenuItem disabled={disabledQuestions.includes('String')} value="String">String</MenuItem>
            <MenuItem disabled={disabledQuestions.includes('Ratio')} value="Ratio">Ratio</MenuItem>
            <MenuItem disabled={disabledQuestions.includes('NumberRange')} value="NumberRange">Number Range</MenuItem>
            <MenuItem disabled={disabledQuestions.includes('SingleChoiceWithText')} value="SingleChoiceWithText">Single Choice With Text</MenuItem>
            <MenuItem disabled={disabledQuestions.includes('MultiChoiceWithText')} value="MultiChoiceWithText">Multiple Choice With Text</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>
      <Box sx={{ px: 3, mt: 3 }}>
        {
          questionType === QuestionTypeV2.String && (
            <StringQuestion
              mode="edit"
              surveyId={surveyId}
              sectionId={sectionId}
              addQuestion={addQuestion}
              disableHideAggregation={disableHideAggregation}
            />
          )
        }
        {
          questionType === QuestionTypeV2.SingleChoice && (
            <SingleChoiceQuestion mode="edit" disableHideAggregation={disableHideAggregation} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.MultiChoice && (
            <MultiChoiceQuestion mode="edit" disableHideAggregation={disableHideAggregation} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.Ratio && (
            <RatioQuestion mode="edit" disableHideAggregation={disableHideAggregation} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.NumberRange && (
            <NumberRangeQuestion mode="edit" disableHideAggregation={disableHideAggregation} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.ChoiceWithText && (
            <ChoiceWithTextQuestion mode="edit" surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.SingleChoiceWithText && (
            <SingleChoiceWithTextQuestion mode="edit" disableHideAggregation={disableHideAggregation} enableTNA={enableTNA} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
        {
          questionType === QuestionTypeV2.MultiChoiceWithText && (
            <MultiChoiceWithTextQuestion mode="edit" disableHideAggregation={disableHideAggregation} enableTNA={enableTNA} surveyId={surveyId} sectionId={sectionId} addQuestion={addQuestion} />
          )
        }
      </Box>
    </Paper>
  );
};

export default QuestionAdder;
